import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTerminology } from "../hooks/api/locale/useTerminology";
import {
  applyTerminologyOverrides,
  changeLanguage,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SupportedLanguage
} from "../i18n/i18nConfig";
import { useAppData } from "./AppDataProvider";

/**
 * Locale context value available to all components.
 */
interface LocaleContextValue {
  /** Current language code (e.g., 'en', 'fr') */
  language: SupportedLanguage;

  /** Change the current language */
  setLanguage: (lang: SupportedLanguage) => void;

  /** List of supported languages */
  supportedLanguages: readonly SupportedLanguage[];

  /** Whether terminology is still loading */
  isLoading: boolean;

  /** Translation function - use translate("key") to get localized strings */
  translate: (key: string, options?: Record<string, unknown>) => string;

  /** Hash of current terminology (for cache validation) */
  terminologyHash: string | null;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
  children: ReactNode;
}

/**
 * LocaleProvider initializes i18n and manages language/terminology.
 *
 * Features:
 * - Fetches organization's default language from settings
 * - Fetches and applies organization-specific terminology overrides
 * - Provides translation function and language switching
 *
 * @example
 * // In index.tsx, wrap your app:
 * <LocaleProvider>
 *   <App />
 * </LocaleProvider>
 *
 * // In components, use the hook:
 * const { t, language, setLanguage } = useLocale();
 * return <button>{t('common.save')}</button>;
 */
export function LocaleProvider({ children }: LocaleProviderProps) {
  const { t, i18n } = useTranslation();
  const { organization, user } = useAppData();
  const [isInitialized, setIsInitialized] = useState(false);

  // Get effective language: user preference > organization default > 'en'
  const effectiveLanguage = useMemo(() => {
    // First check user's preference
    const userLang = user?.preferredLanguageCode || user?.organizationDefaultLanguageCode;
    if (userLang && SUPPORTED_LANGUAGES.includes(userLang as SupportedLanguage)) {
      return userLang as SupportedLanguage;
    }

    // Fall back to organization default
    const orgLang = organization?.defaultLanguageCode;
    if (orgLang && SUPPORTED_LANGUAGES.includes(orgLang as SupportedLanguage)) {
      return orgLang as SupportedLanguage;
    }

    return DEFAULT_LANGUAGE;
  }, [
    user?.preferredLanguageCode,
    user?.organizationDefaultLanguageCode,
    organization?.defaultLanguageCode
  ]);

  // Fetch terminology overrides
  const terminologyQuery = useTerminology({
    enabled: !!organization // Only fetch when org is loaded
  });

  // Set language from user preference or organization settings (initial and on change)
  useEffect(() => {
    if (organization) {
      changeLanguage(effectiveLanguage);
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [organization, user, effectiveLanguage]);

  // Apply terminology overrides when they're loaded
  useEffect(() => {
    if (terminologyQuery.data?.terminology && isInitialized) {
      // Apply overrides to the current language
      applyTerminologyOverrides(effectiveLanguage, terminologyQuery.data.terminology);
    }
  }, [terminologyQuery.data, effectiveLanguage, isInitialized]);

  // Language change handler
  const setLanguage = (lang: SupportedLanguage) => {
    changeLanguage(lang);
    // Re-apply terminology overrides for the new language
    if (terminologyQuery.data?.terminology) {
      applyTerminologyOverrides(lang, terminologyQuery.data.terminology);
    }
  };

  const value = useMemo<LocaleContextValue>(
    () => ({
      language: (i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE,
      setLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES,
      isLoading: terminologyQuery.isLoading,
      translate: (key: string, options?: Record<string, unknown>) => t(key, options),
      terminologyHash: terminologyQuery.data?.hash || null
    }),
    [i18n.language, terminologyQuery.isLoading, terminologyQuery.data?.hash, t]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Hook to access locale context (for function components).
 *
 * @example
 * const { t, language, setLanguage } = useLocale();
 *
 * return (
 *   <div>
 *     <h1>{t('dashboard.title')}</h1>
 *     <select value={language} onChange={(e) => setLanguage(e.target.value)}>
 *       <option value="en">English</option>
 *       <option value="fr">Français</option>
 *     </select>
 *   </div>
 * );
 */
export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

/**
 * Export context for class components to use via static contextType.
 */
export { LocaleContext };

/**
 * Props injected by withLocale HOC into class components.
 *
 * @example
 * interface MyComponentProps extends WithLocaleProps {
 *   title: string;
 * }
 *
 * class MyComponentBase extends Component<MyComponentProps> {
 *   render() {
 *     const { translate } = this.props;
 *     return <button>{translate("common.save")}</button>;
 *   }
 * }
 *
 * export const MyComponent = withLocale(MyComponentBase);
 */
export interface WithLocaleProps {
  /** Translation function - use translate("key") to get localized strings */
  translate: (key: string, options?: Record<string, unknown>) => string;
}

export function withLocale<P extends WithLocaleProps>(
  WrappedComponent: React.ComponentType<P>
): React.ForwardRefExoticComponent<
  React.PropsWithoutRef<Omit<P, keyof WithLocaleProps>> & React.RefAttributes<any>
> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";

  const ComponentWithLocale = React.forwardRef<any, Omit<P, keyof WithLocaleProps>>(
    (props, ref) => {
      const { translate } = useLocale();
      return <WrappedComponent {...(props as unknown as P)} translate={translate} ref={ref} />;
    }
  );

  ComponentWithLocale.displayName = `withLocale(${displayName})`;
  return ComponentWithLocale;
}
